import React, { useEffect, useState, useCallback } from "react";
import styles from "./CustomerOrders.module.css";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import WebService from "../Services/WebService";
// removed NoDataAvailable — we render the empty state inside the table now
import Paginations from "../Customs/pagination/Paginations";
import { useSelector } from "react-redux";
import type { RootState } from "../Services/Store";
import { useNavigate } from "react-router-dom";

// ---- Types ----
interface SaleOrder {
  sale_order_id: number | string;
  order_no?: string;
  customer_id?: string | number;
  user_id?: string | number;
  order_date?: string;
  created_at?: string;
  username?: string;
  total_amount?: number;
  final_amount?: number;
  paid_amount?: number;
  due_amount?: number;
  status: string;
  token_amount?: number | string;
  payment_method?: string;
  payment_mode?: string;
  payment_type?: string;
  method?: string;
  payment?: { method?: string; token_amount?: number | string };
  payment_details?: { method?: string; token_amount?: number | string };
}

interface Pagination {
  current_page: number;
  page_size: number;
  total_records: number;
  total_pages: number;
}

const ORDER_PROGRESS_STEPS = [
  "CHECKOUT",
  "PLACED",
  "PENDING",
  "IN_PROCESSING",
  "SHIPPED",
  "COMPLETED",
] as const;

const STATUS_TABS = ["ALL", ...ORDER_PROGRESS_STEPS, "CANCELED", "REJECTED"];

const STATUS_LABELS: Record<string, string> = {
  ALL: "All",
  CHECKOUT: "Checkout",
  PLACED: "Placed",
  PENDING: "Pending",
  ACCEPTED: "Accepted",
  PENDING_PAYMENT: "Pending Payment",
  PAYMENT_SUBMITTED: "Payment Submitted",
  IN_PROCESSING: "In Processing",
  SHIPPED: "Shipped",
  COMPLETED: "Completed",
  CANCELED: "Canceled",
  REJECTED: "Rejected",
};

const STATUS_COLORS: Record<string, string> = {
  ALL: "#6B7280",
  CHECKOUT: "#D1D5DB",
  PLACED: "#FFD400",
  PENDING: "#FFA600",
  ACCEPTED: "#FFA600",
  PENDING_PAYMENT: "#FFA600",
  PAYMENT_SUBMITTED: "#FFD400",
  IN_PROCESSING: "#7FB5FF",
  SHIPPED: "#7FB5FF",
  COMPLETED: "#10973b",
  CANCELED: "#F04848",
  REJECTED: "#AD1515",
  default: "#DDD",
};

const isProgressStatus = (status: string) =>
  ORDER_PROGRESS_STEPS.includes(status as any);

const currency = (amt: number | string | undefined) => {
  const val = typeof amt === "string" ? parseFloat(amt) : amt;
  return typeof val === "number" && !isNaN(val)
    ? `₹${val.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
    : "-";
};

const formatDate = (dateStr?: string) => {
  if (!dateStr || dateStr === "0001-01-01T00:00:00Z") return "—";
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

const pretty = (s?: string) =>
  s ? s.toString().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—";

const CUSTOMER_TAB_KEY = "customer_active_tab";

const CustomerOrders: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();

  // ---- UI state ----
  const [tab, setTab] = useState<string>(() => {
    const persisted = localStorage.getItem(CUSTOMER_TAB_KEY);
    return persisted && STATUS_TABS.includes(persisted) ? persisted : "ALL";
  });
  const [orders, setOrders] = useState<SaleOrder[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  // search: ONLY order_id_like (numeric)
  const [orderIdSearch, setOrderIdSearch] = useState("");

  // date filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [, setHasFetchedOnce] = useState(false);

  const getNoDataText = () => {
    if (orderIdSearch) return "No orders match this Order ID.";
    if (fromDate || toDate) return "No orders in this date range.";
    return "No orders found.";
  };

  // PLACED-only UI logic is preserved
  const isPlacedTab = tab === "PLACED";

  // persist tab
  useEffect(() => {
    localStorage.setItem(CUSTOMER_TAB_KEY, tab);
  }, [tab]);

  const buildQuery = () => {
    const parts = [`page_number=${page}`];
    if (tab && tab !== "ALL") {
      parts.push(`status=${encodeURIComponent(tab)}`);
    }
    if (orderIdSearch) parts.push(`order_id_like=${encodeURIComponent(orderIdSearch)}`);
    if (fromDate) parts.push(`from_date=${fromDate}`);
    if (toDate) parts.push(`to_date=${toDate}`);
    return parts.join("&");
  };

  const fetchOrders = useCallback(async () => {
    const userId = user?.user_id;
    if (!userId) {
      setApiError("User not found. Please sign in again.");
      setOrders([]);
      setPagination(null);
      setHasFetchedOnce(true);
      return;
    }

    setLoading(true);
    setApiError("");

    try {
      const endpoint = "/v1/door-management/saleorders/my-saleorders";
      const query = buildQuery();
      const action = query ? `${endpoint}?${query}` : endpoint;

      const res = await WebService.getAPI<any>({ action });

      // Defensive about nesting
      const root = res?.data ?? res;
      const data = root?.data ?? root;
      const ordersData = Array.isArray(data?.sale_orders) ? data.sale_orders : [];

      setOrders(ordersData);

      const p = data?.pagination;
      setPagination(p && p.total_pages > 0 ? p : null);

      setHasFetchedOnce(true);
    } catch {
      setOrders([]);
      setPagination(null);
      setApiError("Failed to load your orders. Please try again later.");
      setHasFetchedOnce(true);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, tab, page, orderIdSearch, fromDate, toDate]);

  // initial + on deps change
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // auto-refresh on tab focus/visibility
  useEffect(() => {
    const onFocus = () => fetchOrders();
    const onVis = () => {
      if (document.visibilityState === "visible") fetchOrders();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchOrders]);

  const ExceptionStatusBadge = ({ status }: { status: string }) => (
    <span
      className={styles.statusBadge}
      style={{
        background: STATUS_COLORS[status] || "#bbb",
        color: "#fff",
      }}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );

  // simple step progress up to COMPLETED
  const ProgressBar = ({ status }: { status: string }) => {
    if (!isProgressStatus(status)) return null;
    const activeIndex = ORDER_PROGRESS_STEPS.indexOf(status as any); // 0..5
    return (
      <div className={styles.progressBarWrapper}>
        {ORDER_PROGRESS_STEPS.map((_, i) => {
          const isPast = i < activeIndex;
          const isActive = i === activeIndex;
          const dotClass =
            isActive || isPast ? styles.progressStepActive : styles.progressStepInactive;
          return (
            <span
              key={i}
              className={dotClass}
              title={STATUS_LABELS[ORDER_PROGRESS_STEPS[i]]}
            >
              {isPast ? <FaCheckCircle size={12} /> : null}
            </span>
          );
        })}
      </div>
    );
  };

  const getDateForRow = (o: SaleOrder) =>
    formatDate(o.order_date) !== "—" ? formatDate(o.order_date) : formatDate(o.created_at);

  // where to read token amount & payment method
  const getTokenAmount = (o: SaleOrder): number | string | undefined => {
    const direct =
      o.token_amount ??
      o.payment?.token_amount ??
      o.payment_details?.token_amount ??
      o.paid_amount;
    return direct;
  };

  const getPaymentMethod = (o: SaleOrder): string => {
    const methodRaw =
      o.payment_method ||
      o.payment_mode ||
      o.payment_type ||
      o.method ||
      o.payment?.method ||
      o.payment_details?.method;
    return pretty(methodRaw) || "—";
  };

  // dynamic colSpan so the empty/error/loading row fits header col count
  const visibleColCount = 7 + (isPlacedTab ? 2 : 0);

  return (
    <div className={styles.salesWrapper}>
      {/* Header: Order ID search + date range */}
      <div className={styles.headerRow}>
        <div className={styles.searchSection}>
          {/* Order ID search (numeric only) */}
          <div className={styles.orderIdSearchBox}>
            <input
              type="number"
              className={styles.orderIdInput}
              placeholder="Order ID..."
              value={orderIdSearch}
              onChange={(e) => {
                let v = e.target.value.replace(/[^0-9]/g, "");
                if (v.length > 15) v = v.slice(0, 15);
                setOrderIdSearch(v);
                setPage(1);
              }}
              inputMode="numeric"
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              onKeyDown={(e) =>
                ["e", "E", ".", "-", "+", ","].includes(e.key) && e.preventDefault()
              }
              min="1"
              step="1"
              maxLength={15}
            />
            {orderIdSearch && (
              <FaTimesCircle
                className={styles.clearSearch}
                onClick={() => {
                  setOrderIdSearch("");
                  setPage(1);
                }}
                style={{ cursor: "pointer", marginLeft: -24 }}
              />
            )}
          </div>

          {/* Date range */}
          <div className={styles.dateFilters}>
            <input
              type="date"
              className={styles.dateInput}
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              max={toDate || undefined}
              placeholder="From date"
            />
            <span style={{ margin: "0 5px" }}>to</span>
            <input
              type="date"
              className={styles.dateInput}
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              min={fromDate || undefined}
              placeholder="To date"
            />
            {(fromDate || toDate) && (
              <button
                className={styles.clearDateBtn}
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                  setPage(1);
                }}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabsRow}>
        {STATUS_TABS.map((key) => (
          <button
            key={key}
            className={`${styles.tabBtn} ${tab === key ? styles.tabActive : ""}`}
            onClick={() => {
              setTab(key);
              setPage(1);
            }}
          >
            {STATUS_LABELS[key] || key}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Order No</th>
              <th>Order Date</th>
              <th>Amount</th>

              {/* Show Token + Method only on PLACED tab (unchanged) */}
              {isPlacedTab && (
                <>
                  <th>Token Amount</th>
                  <th>Payment Method</th>
                </>
              )}

              <th>Paid Amount</th>
              <th>Due Amount</th>
              <th>Status</th>
              <th>Progress</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleColCount} style={{ padding: 24, textAlign: "center", color: "#999", fontWeight: 600 }}>
                  Loading...
                </td>
              </tr>
            ) : apiError ? (
              <tr>
                <td colSpan={visibleColCount} style={{ padding: 24, textAlign: "center", color: "#E13D22", fontWeight: 600 }}>
                  {apiError}
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={visibleColCount} style={{ padding: 24, textAlign: "center", color: "#666", fontWeight: 600 }}>
                  {getNoDataText()}
                </td>
              </tr>
            ) : (
              orders.map((o) => (
                <tr
                  key={o.sale_order_id}
                  className={styles.clickableRow}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/mysaleorder-management/${o.sale_order_id}`)}
                >
                  <td>{o.order_no || o.sale_order_id}</td>
                  <td>{getDateForRow(o)}</td>
                  <td>{currency(o.final_amount ?? o.total_amount)}</td>

                  {/* PLACED-only cells (unchanged) */}
                  {isPlacedTab && (
                    <>
                      <td>{currency(getTokenAmount(o))}</td>
                      <td>{getPaymentMethod(o)}</td>
                    </>
                  )}

                  <td>{currency(o.paid_amount)}</td>
                  <td className={Number(o.due_amount) > 0 ? styles.dueRed : ""}>
                    {currency(o.due_amount)}
                  </td>
                  <td>
                    {isProgressStatus(o.status) ? (
                      <span
                        className={styles.statusBadge}
                        style={{
                          background: STATUS_COLORS[o.status] || STATUS_COLORS.default,
                          color: o.status === "CHECKOUT" ? "#333" : "#fff",
                        }}
                      >
                        {STATUS_LABELS[o.status] || o.status}
                      </span>
                    ) : (
                      <ExceptionStatusBadge status={o.status} />
                    )}
                  </td>
                  <td>
                    {isProgressStatus(o.status) ? (
                      <ProgressBar status={o.status} />
                    ) : (
                      <ExceptionStatusBadge status={o.status} />
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <Paginations
          totalItems={pagination.total_records}
          itemsCountPerPage={pagination.page_size}
          changePage={setPage}
          changeCurrentPage={pagination.current_page}
        />
      )}
    </div>
  );
};

export default CustomerOrders;
